import type { Message, ToolId } from '../types'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(payload?.error || '요청을 처리하지 못했어요.')
  }
  return payload as T
}

export type CreditModel = 'default' | 'smart' | 'advanced'
export interface CreditState { limit: number; used: number; remaining: number; costs: Record<CreditModel, number>; premiumDiscount: number; resetsAt?: string; cost?: number; model?: CreditModel }

export async function getCredits() {
  return request<CreditState>('/api/credits')
}

export async function consumeCredits(model: CreditModel) {
  return request<CreditState>('/api/credits/consume', { method: 'POST', body: JSON.stringify({ model }) })
}

export async function askSove(input: { mode: ToolId; message: string; history: Message[]; model: CreditModel }, signal?: AbortSignal) {
  return request<{ content: string; configured: boolean }>('/api/chat', {
    method: 'POST',
    signal,
    body: JSON.stringify(input),
  })
}

export async function scanSecurity(content: string) {
  return request<{ content: string; findings: Array<{ severity: string; title: string; detail: string }> }>('/api/security/scan', {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

export async function searchResearch(query: string) {
  return request<{ content: string; live: boolean; providers?: string[]; sources?: Array<{ title: string; url: string; snippet?: string; provider?: string; date?: string }> }>('/api/research', {
    method: 'POST',
    body: JSON.stringify({ query }),
  })
}

export async function searchMap(query: string) {
  return request<{ configured: boolean; content: string; mapUrl?: string; places?: Array<{ name: string; address: string; rating?: number }> }>('/api/maps/search', {
    method: 'POST',
    body: JSON.stringify({ query }),
  })
}

export async function analyzeImage(filename: string, dataUrl: string) {
  return request<{ configured: boolean; content: string }>('/api/images/analyze', {
    method: 'POST',
    body: JSON.stringify({ filename, dataUrl }),
  })
}

export async function generateImage(prompt: string) {
  return request<{ configured: boolean; content: string; imageUrl?: string }>('/api/images/generate', {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  })
}

export async function createVideoPlan(prompt: string) {
  return request<{ configured: boolean; content: string; videoUrl?: string; plan?: string; storyboard: Array<{ shot: string; visual: string; camera: string; audio: string; duration: string }> }>('/api/video/create', {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  })
}

export async function editImage(filename: string, dataUrl: string, prompt: string) {
  return request<{ configured: boolean; content: string; imageUrl?: string }>('/api/images/edit', {
    method: 'POST',
    body: JSON.stringify({ filename, dataUrl, prompt }),
  })
}
